// src/utils/exportToExcel.js
import ExcelJS from 'exceljs';
import { exportBlockedInDemoMode } from '@/utils/demoSpoof';

/**
 * Exports array data to Excel file with formatting.
 *
 * Single-sheet (original API):
 *   exportToExcel(rows, 'file.xlsx', { sheetName, fontSize, columnWidths })
 *
 * Multi-sheet:
 *   exportToExcel(
 *     [sheet1Rows, sheet2Rows],
 *     'file.xlsx',
 *     { sheetNames: ['Sheet1', 'Sheet2'], columnWidths: [widths1, widths2] }
 *   )
 *
 * @param {Array} data - Array of objects (single sheet) or array of arrays of objects (multi-sheet)
 * @param {String} fileName - Name of the Excel file
 * @param {Object} options - Optional configuration
 * @param {String} options.sheetName - Name of the worksheet (single-sheet mode)
 * @param {Array<String>} options.sheetNames - Names per sheet (multi-sheet mode)
 * @param {Number} options.fontSize - Font size for data cells
 * @param {Object|Array<Object>} options.columnWidths - Column widths (single object or array per sheet)
 */
export const exportToExcel = async (data, fileName, options = {}) => {
  if (exportBlockedInDemoMode()) return;
  if (!data || !data.length) {
    return;
  }

  const { fontSize = 9 } = options;
  const workbook = new ExcelJS.Workbook();

  // Detect multi-sheet: data is an array of arrays
  const isMultiSheet = Array.isArray(data[0]) && Array.isArray(data);
  const sheets = isMultiSheet ? data : [data];
  const sheetNames = isMultiSheet
    ? (options.sheetNames || sheets.map((_, i) => `Sheet ${i + 1}`))
    : [options.sheetName || 'Data'];
  const columnWidthsList = isMultiSheet
    ? (Array.isArray(options.columnWidths) ? options.columnWidths : sheets.map(() => options.columnWidths || {}))
    : [options.columnWidths || {}];

  for (let si = 0; si < sheets.length; si++) {
    const sheetData = sheets[si];
    if (!sheetData || !sheetData.length) {
      // Still create the sheet with a "No data" message so it's visible
      const ws = workbook.addWorksheet(sheetNames[si] || `Sheet ${si + 1}`);
      ws.getCell('A1').value = 'No data available';
      ws.getCell('A1').font = { size: fontSize, italic: true, color: { argb: 'FF999999' } };
      continue;
    }

    const name = sheetNames[si] || `Sheet ${si + 1}`;
    const columnWidths = columnWidthsList[si] || {};
    const worksheet = workbook.addWorksheet(name);

    // Get headers from the first data object
    const headers = Object.keys(sheetData[0]);

    // Define columns with widths
    // AG Grid uses pixel widths, ExcelJS uses character widths (~7 pixels per char)
    worksheet.columns = headers.map(header => ({
      header: header,
      key: header,
      width: columnWidths[header] ? columnWidths[header] / 7 : 75 / 7
    }));

    // Style header row - green background with white bold text
    const headerRow = worksheet.getRow(1);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: fontSize + 1 };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF4CAF50' }
      };
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      cell.border = {
        top: { style: 'thick', color: { argb: 'FF000000' } },
        bottom: { style: 'thick', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } }
      };
    });

    // Helper to sanitize values for Excel XML
    const sanitizeValue = (val) => {
      if (val === null || val === undefined) return '';
      if (typeof val === 'object') val = JSON.stringify(val);
      if (typeof val === 'string') {
        // Remove control characters that break XML (except tab, newline, carriage return)
        val = val.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
      }
      return val;
    };

    // Add data rows
    sheetData.forEach(rowData => {
      const rowValues = {};
      headers.forEach(header => {
        rowValues[header] = sanitizeValue(rowData[header]);
      });
      const row = worksheet.addRow(rowValues);

      // Style data row
      row.eachCell((cell) => {
        cell.font = { size: fontSize };
        cell.alignment = { vertical: 'middle', wrapText: true };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        };
      });
    });
  }

  // Generate and download file
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName || 'export.xlsx';
  link.click();
  URL.revokeObjectURL(link.href);
};

/**
 * Converts a server-streamed CSV blob into a styled Excel download.
 *
 * Use this when the backend streams CSV for performance (avoiding openpyxl
 * timeout on large datasets) but the user should receive .xlsx.
 *
 * @param {Blob} csvBlob - The CSV blob from an axios { responseType: 'blob' } call
 * @param {String} fileName - Download filename (should end in .xlsx)
 * @param {Object} options
 * @param {String} options.sheetName - Worksheet name (default: 'Data')
 * @param {Number} options.fontSize - Font size for data cells (default: 9)
 */
export const csvBlobToExcel = async (csvBlob, fileName, options = {}) => {
  if (exportBlockedInDemoMode()) return;

  const text = await csvBlob.text();
  if (!text.trim()) return;

  const rows = [];
  let current = '';
  let inQuotes = false;
  const fields = [];

  for (let i = 0; i <= text.length; i++) {
    const ch = text[i];
    if (ch === '"') {
      if (inQuotes && text[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if ((ch === ',' || ch === '\n' || ch === '\r' || i === text.length) && !inQuotes) {
      fields.push(current);
      current = '';
      if (ch === '\n' || ch === '\r' || i === text.length) {
        if (fields.length > 1 || fields[0] !== '') rows.push([...fields]);
        fields.length = 0;
        if (ch === '\r' && text[i + 1] === '\n') i++;
      }
    } else if (ch !== undefined) {
      current += ch;
    }
  }

  if (rows.length < 2) return;

  const { fontSize = 9, sheetName = 'Data' } = options;
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(sheetName);

  const headers = rows[0];
  worksheet.columns = headers.map((h) => ({
    header: h,
    key: h,
    width: Math.max(12, h.length + 2),
  }));

  const headerRow = worksheet.getRow(1);
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: fontSize + 1 };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF4CAF50' },
    };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.border = {
      top: { style: 'thick', color: { argb: 'FF000000' } },
      bottom: { style: 'thick', color: { argb: 'FF000000' } },
      left: { style: 'thin', color: { argb: 'FF000000' } },
      right: { style: 'thin', color: { argb: 'FF000000' } },
    };
  });

  for (let r = 1; r < rows.length; r++) {
    const values = {};
    rows[r].forEach((val, ci) => {
      if (ci < headers.length) values[headers[ci]] = val;
    });
    worksheet.addRow(values);
  }

  worksheet.views = [{ state: 'frozen', ySplit: 1 }];

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName || 'export.xlsx';
  link.click();
  URL.revokeObjectURL(link.href);
};
